// src/features/admin/components/settings/document-management/components/FontManager.tsx
import React, { useState } from 'react';
import {
  DocumentTextIcon,
  Squares2X2Icon,
  ArrowUpTrayIcon,
  Cog6ToothIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import FontRegistry from './font-manager/FontRegistry';
import FontUpload from './font-manager/FontUpload';
import FontSettings from './font-manager/FontSettings';
import FontStatistics from './font-manager/FontStatistics';

type FontManagerSection = 'registry' | 'upload' | 'settings' | 'statistics';

/**
 * Font Manager Component
 * 
 * Comprehensive font management for firm administrators:
 * - View and manage available fonts
 * - Upload new fonts
 * - Configure default fonts and template assignments
 * - View font usage statistics
 */
const FontManager: React.FC = () => {
  const [activeSection, setActiveSection] = useState<FontManagerSection>('registry');

  const sections = [
    {
      id: 'registry' as FontManagerSection,
      label: 'Font Registry',
      icon: Squares2X2Icon,
      description: 'View, enable, and manage available fonts'
    },
    {
      id: 'upload' as FontManagerSection,
      label: 'Upload Fonts',
      icon: ArrowUpTrayIcon,
      description: 'Add new fonts to your firm\'s library'
    },
    {
      id: 'settings' as FontManagerSection,
      label: 'Default Fonts',
      icon: Cog6ToothIcon,
      description: 'Configure default fonts for templates and document types'
    },
    {
      id: 'statistics' as FontManagerSection,
      label: 'Usage Stats',
      icon: ChartBarIcon,
      description: 'View font usage analytics across your firm'
    }
  ];

  return (
    <div className="font-manager">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center mb-2">
          <DocumentTextIcon className="h-6 w-6 text-lex-accent mr-2" />
          Font Manager
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Manage fonts available to users in the EdgeDocs toolbar. Configure court-compliant fonts, upload custom typefaces, and control font accessibility across templates and document types.
        </p>
      </div>

      {/* Sub-navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-6">
            {sections.map((section) => {
              const IconComponent = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`group inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isActive
                      ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                  title={section.description}
                >
                  <IconComponent className={`h-5 w-5 mr-2 ${
                    isActive 
                      ? 'text-lex-accent dark:text-lex-accent' 
                      : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500'
                  }`} />
                  {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content sections */}
      <div className="min-h-[500px]">
        {activeSection === 'registry' && <FontRegistry />}
        {activeSection === 'upload' && <FontUpload />}
        {activeSection === 'settings' && <FontSettings />}
        {activeSection === 'statistics' && <FontStatistics />}
      </div>
    </div>
  );
};

export default FontManager;

